import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';

/**
 * Browsing the single DynamoDB table.
 *
 * Read-only, and the screen says so rather than leaving it to be discovered:
 * every write in this application goes through a repository that also maintains
 * uniqueness reservations, lookup items and the aggregates the stream keeps
 * current, so an edit made here would corrupt invariants nothing else checks.
 *
 * Counts are labelled approximate because they are. DynamoDB refreshes
 * ItemCount roughly every six hours, and the honest alternative — scanning the
 * table on every page load to print an exact number nobody acts on — costs
 * real money on a table of any size.
 */

interface Attribute {
  name: string;
  type: string;
  required: boolean;
  readOnly: boolean;
  redacted: boolean;
}

interface EntityIndex {
  accessPattern: string;
  index: string;
  partition: string[];
  sort: string[];
}

interface EntityDescription {
  name: string;
  attributes: Attribute[];
  indexes: EntityIndex[];
}

interface TableIndex {
  name: string;
  status: string;
  approximateItemCount: number;
  approximateSizeBytes: number;
  projection: string;
}

interface TableOverview {
  name: string;
  status: string;
  approximateItemCount: number;
  approximateSizeBytes: number;
  billingMode: string;
  keys: { attribute: string; role: string }[];
  indexes: TableIndex[];
  stream: { enabled: boolean; viewType?: string };
  pointInTimeRecovery: string | null;
}

interface MetricSeries {
  key: string;
  label: string;
  name: string;
  unit: string;
  points: { at: string; value: number }[];
  total: number | null;
  error?: string;
}

type Record_ = Record<string, unknown>;

const bytes = (n: number) => {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  return `${(n / 1024 ** i).toFixed(i ? 1 : 0)} ${units[i]}`;
};

/** A cell's worth of a value, without pretending a nested object is a string. */
const preview = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return Array.isArray(value) ? `[${value.length}]` : '{…}';
  const text = String(value);
  return text.length > 48 ? `${text.slice(0, 48)}…` : text;
};

const DatabaseComponent: React.FC = () => {
  const [table, setTable] = useState<TableOverview | null>(null);
  const [schemas, setSchemas] = useState<EntityDescription[]>([]);
  const [metrics, setMetrics] = useState<MetricSeries[]>([]);
  const [metricsWindow, setMetricsWindow] = useState(24);

  const [selected, setSelected] = useState<string | null>(null);
  const [records, setRecords] = useState<Record_[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [searchExhausted, setSearchExhausted] = useState(false);
  const [openRecord, setOpenRecord] = useState<Record_ | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadOverview = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiService.getDatabaseOverview();
      setTable(res?.data?.table ?? null);
      setSchemas(res?.data?.entities ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to read the table');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMetrics = useCallback(async (hours: number) => {
    try {
      const res = await apiService.getDatabaseMetrics(hours);
      setMetrics(res?.data?.series ?? []);
    } catch {
      // The panel reports its own unavailability below; a failure here should
      // not take the rest of the screen with it.
      setMetrics([]);
    }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadMetrics(metricsWindow); }, [loadMetrics, metricsWindow]);

  const openEntity = async (name: string, nextCursor: string | null = null) => {
    setSelected(name);
    setOpenRecord(null);
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiService.getDatabaseRecords(name, { limit: 25, cursor: nextCursor });
      const data = res?.data;
      setRecords(nextCursor ? [...records, ...(data?.records ?? [])] : (data?.records ?? []));
      setCursor(data?.cursor ?? null);
      setHasMore(Boolean(data?.hasMore));
      setSearchExhausted(Boolean(data?.searchExhausted));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to read records');
    } finally {
      setIsLoading(false);
    }
  };

  const schema = schemas.find((s) => s.name === selected) || null;
  const columns = schema
    ? schema.attributes.slice(0, 6).map((a) => a.name)
    : Object.keys(records[0] ?? {}).slice(0, 6);

  return (
    <div className="space-y-6">
      <div className="bg-info-bg border border-border-default rounded-xl p-4">
        <p className="text-ink-2 text-sm">
          <strong className="text-ink">Read-only.</strong> Writes go through the repositories,
          which also maintain uniqueness reservations, lookup rows and the aggregates the change
          stream keeps current — a row changed here would break invariants nothing else checks.
          Every listing and record you open is recorded with your username.
        </p>
      </div>

      {error && (
        <div className="bg-negative-bg border border-negative/30 text-negative rounded-xl p-4" role="alert">
          {error}
        </div>
      )}

      {table && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-surface rounded-2xl p-4 border border-border-subtle shadow-sm">
              <h3 className="text-sm font-semibold text-ink-2 mb-1">Table</h3>
              <div className="text-lg font-bold text-ink break-all">{table.name}</div>
              <p className="text-ink-3 text-xs mt-1">{table.status} · {table.billingMode}</p>
            </div>
            <div className="bg-surface rounded-2xl p-4 border border-border-subtle shadow-sm">
              <h3 className="text-sm font-semibold text-ink-2 mb-1">Items</h3>
              <div className="text-3xl font-bold text-ink">
                {table.approximateItemCount?.toLocaleString() ?? '—'}
              </div>
              <p className="text-ink-3 text-xs mt-1">Approximate — refreshed every ~6h</p>
            </div>
            <div className="bg-surface rounded-2xl p-4 border border-border-subtle shadow-sm">
              <h3 className="text-sm font-semibold text-ink-2 mb-1">Size</h3>
              <div className="text-3xl font-bold text-ink">{bytes(table.approximateSizeBytes)}</div>
              <p className="text-ink-3 text-xs mt-1">Approximate</p>
            </div>
            <div className="bg-surface rounded-2xl p-4 border border-border-subtle shadow-sm">
              <h3 className="text-sm font-semibold text-ink-2 mb-1">Stream</h3>
              <div className="text-lg font-bold text-ink">
                {table.stream.enabled ? 'Enabled' : 'Off'}
              </div>
              <p className="text-ink-3 text-xs mt-1">
                {table.stream.viewType || 'No change stream'}
              </p>
            </div>
          </div>

          <div className="bg-surface rounded-2xl p-6 border border-border-subtle shadow-sm">
            <h3 className="text-lg font-title font-bold text-ink mb-3">Indexes</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-ink-2">
                  <tr>
                    <th scope="col" className="py-2 font-semibold">Name</th>
                    <th scope="col" className="py-2 font-semibold">Status</th>
                    <th scope="col" className="py-2 font-semibold">Projection</th>
                    <th scope="col" className="py-2 font-semibold text-right">Items (approx.)</th>
                    <th scope="col" className="py-2 font-semibold text-right">Size</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-border-subtle">
                    <td className="py-2"><code className="text-ink">primary</code></td>
                    <td className="py-2 text-ink-2">{table.status}</td>
                    <td className="py-2 text-ink-2">
                      {table.keys.map((k) => `${k.attribute} (${k.role === 'HASH' ? 'pk' : 'sk'})`).join(', ')}
                    </td>
                    <td className="py-2 text-right text-ink-2">{table.approximateItemCount?.toLocaleString()}</td>
                    <td className="py-2 text-right text-ink-2">{bytes(table.approximateSizeBytes)}</td>
                  </tr>
                  {table.indexes.map((index) => (
                    <tr key={index.name} className="border-t border-border-subtle">
                      <td className="py-2"><code className="text-ink">{index.name}</code></td>
                      <td className="py-2 text-ink-2">{index.status}</td>
                      <td className="py-2 text-ink-2">{index.projection}</td>
                      <td className="py-2 text-right text-ink-2">{index.approximateItemCount?.toLocaleString()}</td>
                      <td className="py-2 text-right text-ink-2">{bytes(index.approximateSizeBytes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-surface rounded-2xl p-6 border border-border-subtle shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="text-lg font-title font-bold text-ink">Metrics</h3>
              <div className="flex gap-2">
                {[24, 72, 168].map((hours) => (
                  <button
                    key={hours}
                    onClick={() => setMetricsWindow(hours)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-semibold ${
                      metricsWindow === hours
                        ? 'bg-surface-active border-border-strong text-ink'
                        : 'bg-surface border-border-subtle text-ink-2 hover:bg-surface-hover'
                    }`}
                  >
                    {hours === 24 ? '24h' : hours === 72 ? '3d' : '7d'}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {metrics.map((series) => {
                // Throttles and errors are the ones that mean something is
                // wrong. Consumed capacity on a pay-per-request table is a
                // cost signal, not a ceiling being approached.
                const isProblem = ['throttledRequests', 'systemErrors', 'userErrors'].includes(series.key);
                const bad = isProblem && (series.total ?? 0) > 0;

                return (
                  <div key={series.key} className="border border-border-subtle rounded-xl p-3">
                    <h4 className="text-xs font-semibold text-ink-2 mb-1" title={series.name}>{series.label}</h4>
                    {series.error ? (
                      <p className="text-caution text-xs">Unavailable</p>
                    ) : (
                      <>
                        <div className={`text-2xl font-bold ${bad ? 'text-negative' : 'text-ink'}`}>
                          {series.total === null ? '—' : Math.round(series.total).toLocaleString()}
                        </div>
                        <p className="text-ink-3 text-xs mt-1">{series.unit} · {series.points.length} pts</p>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {metrics.some((m) => m.error) && (
              <p className="text-ink-3 text-xs mt-3">
                Some series are unavailable. The usual cause is the task role not yet having
                <code className="mx-1">cloudwatch:GetMetricStatistics</code>.
              </p>
            )}
          </div>
        </>
      )}

      <div className="bg-surface rounded-2xl p-6 border border-border-subtle shadow-sm">
        <h3 className="text-lg font-title font-bold text-ink mb-3">Entities</h3>
        <div className="flex flex-wrap gap-2 mb-5">
          {schemas.map((entity) => (
            <button
              key={entity.name}
              onClick={() => openEntity(entity.name)}
              className={`px-3 py-1.5 rounded-lg border text-xs font-semibold ${
                selected === entity.name
                  ? 'bg-surface-active border-border-strong text-ink'
                  : 'bg-surface border-border-subtle text-ink-2 hover:bg-surface-hover'
              }`}
            >
              {entity.name}
            </button>
          ))}
        </div>

        {isLoading && <p className="text-ink-2">Loading…</p>}

        {selected && !isLoading && (
          <>
            {schema && (
              <div className="mb-4 text-xs text-ink-3">
                <span className="font-semibold text-ink-2">Access patterns: </span>
                {schema.indexes.map((i) => `${i.accessPattern} (${i.index})`).join(' · ')}
              </div>
            )}

            {records.length === 0 ? (
              <p className="text-ink-2">No {selected} records.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface-sunken text-left text-ink-2">
                    <tr>
                      {columns.map((c) => (
                        <th scope="col" key={c} className="px-3 py-2 font-semibold">{c}</th>
                      ))}
                      <th scope="col" className="px-3 py-2 font-semibold text-right">Open</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((record, i) => (
                      <tr key={String(record._id ?? record.id ?? i)} className="border-t border-border-subtle">
                        {columns.map((c) => (
                          <td key={c} className="px-3 py-2 text-ink-2">
                            <code className="text-xs">{preview(record[c])}</code>
                          </td>
                        ))}
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => setOpenRecord(record)}
                            className="px-2 py-1 rounded-lg border border-border-default text-ink-2 text-xs font-semibold hover:bg-surface-hover"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {searchExhausted && (
              <p className="text-caution text-xs mt-3">
                Stopped after reading a bounded number of pages without filling this one. More
                records may exist further along — this entity is sparse relative to the table.
              </p>
            )}

            {hasMore && cursor && (
              <button
                onClick={() => openEntity(selected, cursor)}
                className="mt-4 px-4 py-2 rounded-lg border border-border-default text-ink-2 text-sm font-semibold hover:bg-surface-hover"
              >
                Load more
              </button>
            )}
          </>
        )}
      </div>

      {openRecord && (
        <div className="bg-surface rounded-2xl p-6 border border-border-subtle shadow-sm">
          <div className="flex justify-between items-start gap-4 mb-3">
            <h3 className="text-lg font-title font-bold text-ink">Record</h3>
            <button
              onClick={() => setOpenRecord(null)}
              className="text-ink-3 hover:text-ink"
              aria-label="Close record"
            >
              ×
            </button>
          </div>
          <pre className="text-xs text-ink bg-surface-sunken rounded-xl p-4 overflow-x-auto">
            {JSON.stringify(openRecord, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default DatabaseComponent;
